"use client"

import * as React from "react"
import { Sparkles } from "lucide-react"

import { cn } from "../../lib/utils"
import { Badge } from "./badge"

export interface KeywordSuggestionCardProps {
  className?: string
  keywords: string[]
  additionalKeywords?: string[]
  isLoading?: boolean
  onKeywordClick?: (keyword: string) => void
  title: string
  subtitleLine1: string
  showLessLabel: string
  showMoreLabel: string
}

function KeywordSuggestionCard({
  className,
  keywords,
  additionalKeywords = [],
  isLoading = false,
  onKeywordClick,
  title,
  subtitleLine1,
  showLessLabel,
  showMoreLabel,
}: KeywordSuggestionCardProps) {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const remainingCount = additionalKeywords.length

  const handleExpandToggle = () => {
    setIsExpanded((prev) => !prev)
  }

  return (
    <div
      className={cn(
        "bg-white border border-black rounded-lg p-3.5 shadow-[0px_4px_0px_0px_#000000] transition-all duration-300",
        className
      )}
    >
      {/* Header */}
      <div className="flex flex-col gap-1.5 mb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="size-[18px] text-[#ED5AA9]" />
          <span className="font-medium text-sm text-[#ED5AA9]">{title}</span>
        </div>
        <p className="text-xs text-[#667085] leading-4">
          {subtitleLine1}
        </p>
      </div>

      {/* Keywords */}
      <div className="flex flex-wrap gap-2">
        {isLoading ? (
          <>
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="h-7 w-20 bg-gray-100 animate-pulse rounded-full border border-gray-200"
              />
            ))}
          </>
        ) : (
          <>
            {keywords.map((keyword) => (
              <Badge
                key={keyword}
                label={keyword}
                variant="default"
                onClick={() => onKeywordClick?.(keyword)}
                className={onKeywordClick ? "cursor-pointer hover:opacity-80" : ""}
              />
            ))}

            {/* Expanded keywords with smooth animation */}
            <div
              className={cn(
                "flex flex-wrap gap-2 overflow-hidden transition-all duration-300 ease-in-out",
                isExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
              )}
              style={{
                width: isExpanded ? "100%" : "0",
              }}
            >
              {additionalKeywords.map((keyword) => (
                <Badge
                  key={keyword}
                  label={keyword}
                  variant="default"
                  onClick={() => onKeywordClick?.(keyword)}
                  className={onKeywordClick ? "cursor-pointer hover:opacity-80" : ""}
                />
              ))}
            </div>

            {/* Show more / Show less button */}
            {remainingCount > 0 && (
              <Badge
                label={isExpanded ? showLessLabel : showMoreLabel}
                variant="blue"
                onClick={handleExpandToggle}
                className="cursor-pointer hover:opacity-80 transition-all duration-200"
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export { KeywordSuggestionCard }
